import { forwardRef } from 'react'
import { getFormattedValueParts } from '../../../utils/riskProfile.utils'
import { mapRiskProfileSocialEconomicResponseToSummary } from '../../../chart/RiskProfileSocialEconomicChartData'
import type {
  RiskProfilePropertiesResponse,
  PropertiesMetricOption,
} from '../../../types/riskProfileProperties.types'
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip'
import {
  SOCIAL_DEMOGRAPHIC_CHART_COLOUR_VARS,
  SOCIAL_ECONOMIC_FALLBACK_CHART_COLOUR,
} from '../../../constants/riskProfile.constants'

export interface SocialDemographicChartProps {
  data: RiskProfilePropertiesResponse
  metricOption: PropertiesMetricOption
  entityNoun: string
}

export const SocialDemographicChart = forwardRef<HTMLDivElement, SocialDemographicChartProps>(
  ({ data, metricOption, entityNoun }, ref) => {
    const summary = mapRiskProfileSocialEconomicResponseToSummary(data, metricOption)

    if (!summary) {
      return <p className="text-sm text-muted-foreground">No comparable data for this selection.</p>
    }

    const formattedTotal = getFormattedValueParts(summary.totalValue, metricOption, summary.unit)
    const formattedGrandTotal = getFormattedValueParts(
      summary.grandTotalValue,
      metricOption,
      summary.unit,
    )

    return (
      <div ref={ref} className="flex h-full min-h-[350px] flex-col">
        <div className="flex items-baseline gap-2">
          <div className="text-5xl font-semibold leading-none tracking-tight text-foreground">
            {formattedTotal.value}
          </div>
          <div className="text-xl font-normal text-foreground">
            {entityNoun.charAt(0).toUpperCase() + entityNoun.slice(1)} exposed
          </div>
        </div>
        <div className="mt-1 text-base font-normal text-foreground">
          {summary.percentValue !== null &&
            `${summary.percentValue.toLocaleString('en-NZ', { maximumFractionDigits: 0 })}% of `}
          {formattedGrandTotal.value} total {entityNoun}
        </div>

        <div className="mt-8 flex h-10 w-full overflow-hidden rounded-md bg-muted">
          {(() => {
            // Calculate total width if we summed all categories
            const totalCategoriesWidth = summary.categories.reduce(
              (sum, cat) => sum + (cat.value > 0 ? cat.value : 0),
              0,
            )
            // Calculate scaling factor to ensure total colored width matches actual exposed total
            const scalingFactor =
              totalCategoriesWidth > 0 ? summary.totalValue / totalCategoriesWidth : 1

            return summary.categories.map((category, index) => {
              if (category.value <= 0) return null
              // Scale the category value so the total width represents the actual exposed total
              const scaledValue = category.value * scalingFactor
              const exposedWidthPercent =
                summary.grandTotalValue > 0 ? (100 * scaledValue) / summary.grandTotalValue : 0
              return (
                <TooltipProvider key={category.key}>
                  <Tooltip delayDuration={0}>
                    <TooltipTrigger asChild>
                      <div
                        className="h-full"
                        style={{
                          width: `${exposedWidthPercent}%`,
                          backgroundColor:
                            SOCIAL_DEMOGRAPHIC_CHART_COLOUR_VARS[
                              index % SOCIAL_DEMOGRAPHIC_CHART_COLOUR_VARS.length
                            ],
                        }}
                      />
                    </TooltipTrigger>
                    <TooltipContent
                      className={`bg-background text-foreground border border-border relative`}
                      style={{
                        color:
                          SOCIAL_DEMOGRAPHIC_CHART_COLOUR_VARS[
                            index % SOCIAL_DEMOGRAPHIC_CHART_COLOUR_VARS.length
                          ],
                      }}
                    >
                      <div className="absolute top-0 left-0 right-0 bottom-0 flex items-center justify-center" />
                      <p className="font-medium">{category.label}</p>
                      <p className="">
                        {getFormattedValueParts(category.value, metricOption, summary.unit).value} (
                        {category.percentOfTotal.toLocaleString('en-NZ', {
                          maximumFractionDigits: 0,
                        })}
                        %)
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )
            })
          })()}
        </div>

        <dl className="mt-10 grid grid-cols-[repeat(auto-fit,minmax(80px,1fr))] gap-x-4 gap-y-8">
          {summary.categories
            .filter((category) => category.percentOfTotal > 0)
            .map((category, index) => {
              const colour =
                SOCIAL_DEMOGRAPHIC_CHART_COLOUR_VARS[index] ?? SOCIAL_ECONOMIC_FALLBACK_CHART_COLOUR
              const formattedCategoryValue = getFormattedValueParts(
                category.value,
                metricOption,
                summary.unit,
              )

              return (
                <div key={category.key} className="min-w-0">
                  <dt className="h-1 w-full rounded-full" style={{ backgroundColor: colour }} />
                  <dt className="mt-2 text-sm text-muted-foreground">{category.label}</dt>
                  <dd className="mt-2 flex flex-wrap items-baseline gap-x-1 gap-y-1">
                    <span className="min-w-0 text-2xl font-semibold leading-none tracking-tight text-foreground">
                      {formattedCategoryValue.value}
                    </span>
                    <span className="text-sm text-muted-foreground">exposed</span>
                  </dd>
                  <dd className="mt-1 text-xs text-muted-foreground">
                    {category.percentValue !== null &&
                      `${category.percentValue.toLocaleString('en-NZ', { maximumFractionDigits: 0 })}% of ${category.label} ${entityNoun}`}
                  </dd>
                  <dd className="mt-1 text-xs text-muted-foreground">
                    {category.percentOfTotal.toLocaleString('en-NZ', {
                      maximumFractionDigits: 0,
                    })}
                    % of exposed
                  </dd>
                </div>
              )
            })}
        </dl>
      </div>
    )
  },
)

SocialDemographicChart.displayName = 'SocialDemographicChart'
